import type {
  ClosingCondition,
  CreateProjectRiskReviewInput,
  DashboardMyWorkItem,
  DecisionListQuery,
  DecisionWorkflowLaunchStatus,
  DiligenceReport,
  DueDiligenceItem,
  FollowUp,
  Fund,
  InvestmentApproval,
  InvestmentApprovalStatus,
  InvestmentApprovalType,
  InvestmentDashboard,
  InvestmentDecision,
  InvestmentMaterialItem,
  InvestmentProject,
  InvestmentWorkflowConfig,
  InvestmentWorkflowTask,
  InvestmentWorkflowTemplate,
  Lead,
  LeadListQuery,
  LeadListResult,
  LeadScreening,
  LeadScreeningOpinion,
  LeadScreeningRejectionReason,
  LeadStatus,
  LifecycleReadiness,
  ProjectFiling,
  ProjectFollowUp,
  ProjectListQuery,
  ProjectPreMeeting,
  ProjectRiskReview,
  ProjectStage,
  RiskItem,
  SetDecisionResultPayload,
  StateAssetFiling,
  StateAssetFilingInput,
  SubmitDecisionPayload,
  Transaction,
  TransactionAgreement,
  TransactionAgreementPatch,
  TransactionAmendment,
  TransactionListQuery,
  TransactionPayment,
  UpdateFundPayload,
  UpdateProjectPayload,
  UpdateProjectRiskReviewInput,
  UpdateTransactionProgressPayload,
} from "@/modules/investment/features/investment/types";
import { createAppRequest } from "@/services/request";
import type { FollowUpDetails } from "@/shared/projectLifecycle/FormDetails";
import type { CreateProjectPayload } from "@/modules/investment/features/investment/constants/projectCreateFields";
import type { LeadStorageInput } from "@/modules/investment/services/bff/preinvestment.api";

const request = createAppRequest();

export type LeadOwnershipEvent = {
  id: string;
  eventType:
    | "LeadCreated"
    | "LeadAssigned"
    | "LeadClaimed"
    | "LeadReleased"
    | "LeadReassigned";
  occurredAt: string;
  actorMemberUuid: string | null;
  previousOwnerMemberUuid: string | null;
  previousOwnerName: string | null;
  ownerMemberUuid: string | null;
  ownerName: string | null;
  reason: string | null;
};

export type ProjectTransferEvent = {
  id: string;
  occurredAt: string;
  actorMemberUuid: string | null;
  previousMemberUuid: string | null;
  previousName: string | null;
  managerMemberUuid: string | null;
  managerName: string | null;
  reason: string | null;
};

export type ProjectChangeEvent = {
  id: string;
  action: string;
  occurredAt: string;
  actorMemberUuid?: string | null;
  before: Record<string, unknown> | null;
  after: Record<string, unknown> | null;
};

/** Display-only: never send snapshot/legacy name fields as write identity. */
const withProjectFundSnapshot = (
  project: InvestmentProject
): InvestmentProject => ({
  ...project,
  company: project.companyNameSnapshot || project.company || "",
  targetFund: project.fundNameSnapshot || project.targetFund || "",
});

const withDecisionFundSnapshot = (
  decision: InvestmentDecision
): InvestmentDecision => ({
  ...decision,
  targetFund: decision.fundNameSnapshot || decision.targetFund || "",
});

const withTransactionFundSnapshot = (
  transaction: Transaction
): Transaction => ({
  ...transaction,
  fund: transaction.fundNameSnapshot || transaction.fund || "",
});

type PreMeetingResponse = {
  uuid: string;
  projectUuid: string;
  matterVersion: number;
  matterStatus: string;
  title: string;
  meetingType: string;
  meetingFormat: string;
  meetingDate: string;
  meetingEndAt?: string | null;
  attachmentUuid?: string;
  attachmentUuids?: string[];
  attendees: Array<{ memberUuid: string; name: string }>;
  content: string;
  result: string;
  createdAt?: string;
};

export type ProjectChecklistRequirement = {
  requirementCode: string;
  title: string;
  kind: string;
  required: boolean;
  blocking: boolean;
  gateFromStage: string | null;
  gateToStage: string | null;
  milestoneStatusCode: string | null;
  /** 前端流程分区，由定义表 config_json.sectionKey 下发；为空表示不归属任何流程分区。 */
  sectionKey: string | null;
  /** 需要在清单上挂动作面板时由 config_json.actionKey 下发。 */
  actionKey: string | null;
  /** 是否允许走「免予申请」豁免。 */
  exemptible: boolean;
  status:
    | "pending"
    | "in_progress"
    | "submitted"
    | "completed"
    | "waived"
    | "not_applicable";
  outcomeCode: string | null;
  outcomeLabel: string | null;
  progress: Record<string, unknown> | null;
  completedAt: string | null;
  note: string | null;
};

/** 阶段推进缺项，对应后端 getTransitionGuard 的 blocker。 */
export type ProjectChecklistBlocker = {
  requirementCode: string;
  stage?: string;
  fundBatchUuid?: string;
  title: string;
  status: ProjectChecklistRequirement["status"];
  message: string;
  sectionKey?: string | null;
  actionKey?: string | null;
  exemptible?: boolean;
};

/** 推进前预演：`allowed` 为 false 时按 `blockers` 展示缺项。 */
export type ProjectChecklistAdvanceGuard = {
  projectUuid: string;
  fromStage: string;
  targetStage: string;
  allowed: boolean;
  blockers: ProjectChecklistBlocker[];
};

export type ProjectChecklist = {
  projectUuid: string;
  projectStatus: string;
  currentStage: string;
  currentProgressStatus: string | null;
  currentStageStatus: string;
  phaseStatuses: Record<string, string>;
  canAdvance: boolean;
  nextStage: string | null;
  nextStagePreview: ProjectChecklistBlocker[];
  stages: Array<{
    stage: string;
    status: string;
    requirements: ProjectChecklistRequirement[];
  }>;
  milestones: Array<{
    stage: string;
    code: string;
    requirementCode: string;
    title: string;
    status: ProjectChecklistRequirement["status"];
  }>;
  fundBatches: Array<{
    uuid: string;
    fundUuid: string;
    fundName: string;
    sequenceNo: number;
    stage: string;
    workflowStatus: string;
    status: string;
    requirements: ProjectChecklistRequirement[];
  }>;
};

export type ProjectChecklistOverview = {
  total: number;
  byStage: Record<string, number>;
  byMilestone: Record<string, number>;
  items: Array<{
    projectUuid: string;
    projectName: string;
    stage: string;
    projectStatus: string;
    currentStageStatus: string;
    currentProgressStatus: string | null;
    canAdvance: boolean;
  }>;
};

export type ProjectCompliancePledge = {
  uuid: string;
  memberUuid: string;
  memberNameSnapshot: string;
  memberPositionSnapshot: string | null;
  status: "pending" | "signed";
  signedAt: string | null;
  documentAttachmentUuid: string | null;
};

export type ProjectCompliancePledgeResult = {
  projectUuid: string;
  rows: ProjectCompliancePledge[];
  completed: number;
  total: number;
};

export type ProjectCoInvestmentRatio = {
  uuid: string;
  memberUuid: string;
  memberNameSnapshot: string;
  memberPositionSnapshot: string | null;
  ratio: number | null;
};

export type ProjectCoInvestmentRatioResult = {
  projectUuid: string;
  fundBatchUuid: string | null;
  rows: ProjectCoInvestmentRatio[];
  completed: number;
  total: number;
  ratioSum: number;
  completedFlag: boolean;
  notApplicable: boolean;
};

export type ProjectQuestionnaire = {
  uuid: string;
  projectUuid: string;
  templateKind: "medical" | "non_medical";
  status: "in_progress" | "completed";
  memberUuids: string[];
  questionsJson: Array<{ key: string; title: string }>;
  totalScore: number | null;
  summaryUrl: string | null;
  responses: Array<{
    memberUuid: string;
    memberNameSnapshot: string;
    answersJson: Record<string, "A" | "B" | "C">;
    score: number;
  }>;
  completed: number;
  total: number;
};

export type ProjectStageWaiver = {
  uuid: string;
  projectUuid: string;
  stage: string;
  requirementCode: string;
  status: "pending" | "approved" | "rejected";
  reason: string;
  requestedByMemberUuid: string;
  reviewedByMemberUuid: string | null;
  reviewedAt: string | null;
  reviewOpinion: string | null;
};

const withPreMeeting = (record: PreMeetingResponse): ProjectPreMeeting => ({
  id: record.uuid,
  projectId: record.projectUuid,
  version: record.matterVersion,
  matterStatus: record.matterStatus,
  meetingName: record.title,
  meetingType: record.meetingType,
  meetingFormat: record.meetingFormat,
  meetingDate: record.meetingDate,
  meetingEndAt: record.meetingEndAt,
  attendees: (record.attendees || []).map((member) => member.memberUuid),
  attendeeNames: (record.attendees || []).map((member) => member.name),
  content: record.content,
  result: record.result,
  createdAt: record.createdAt,
  attachmentUuid: record.attachmentUuid,
  attachmentUuids: record.attachmentUuids,
});

const toUpdateProjectBody = (data: UpdateProjectPayload) => {
  const {
    targetFund: _legacyFund,
    company: _legacyCompany,
    ...body
  } = data as UpdateProjectPayload & {
    targetFund?: string;
    company?: string;
  };
  return Object.fromEntries(
    Object.entries({
      ...body,
      fundUuid: data.fundUuid,
      companyOrganizationUuid: data.companyOrganizationUuid,
    }).filter(([, value]) => value !== undefined)
  );
};

/** 空值口径：undefined / null / 空串 / 空数组都不提交（后端 DTO 大量 @IsOptional）。 */
const isEmptyCreateValue = (value: unknown): boolean => {
  if (value === undefined || value === null) return true;
  if (typeof value === "string") return value.trim() === "";
  if (Array.isArray(value)) return value.length === 0;
  return false;
};

/**
 * 嵌套对象（`company` 子对象）也按同一口径收敛：后端 `forbidNonWhitelisted: true`，
 * 且 `company.name` 之外的键都是 @IsOptional，塞空串属于「显式传空」，不如直接不发。
 */
const pickCreateValue = (value: unknown): unknown => {
  if (typeof value === "string") return value.trim();
  if (Array.isArray(value)) return value;
  if (value && typeof value === "object") {
    const nested = Object.fromEntries(
      Object.entries(value as Record<string, unknown>)
        .map(([key, item]) => [key, pickCreateValue(item)])
        .filter(([, item]) => !isEmptyCreateValue(item))
    );
    return Object.keys(nested).length ? nested : undefined;
  }
  return value;
};

/**
 * 新建项目请求体：只提交有值的字段（undefined / 空串 / 空数组一律剔除），
 * 后端 DTO 大量字段带 @IsOptional，传 null 反而会命中类型校验。
 * 布尔 false 是有效业务值（如 hasBoardSeat=false、followOn=false），必须保留。
 * `company` / `managers` 这类嵌套结构同样逐层收敛，避免内层空串被当成显式传空。
 */
export const toCreateProjectBody = (data: CreateProjectPayload) =>
  Object.fromEntries(
    Object.entries(data)
      .map(([key, value]) => [key, pickCreateValue(value)])
      .filter(([, value]) => !isEmptyCreateValue(value))
  );

/**
 * 释放时快照的「原责任经理」。
 *
 * 契约来自 409 响应体 `releasedProjects[].formerManagers[]`：
 * `{ memberUuid, name, role }`，role 口径与 `ProjectMemberRole` 一致（主责 / 非主责）。
 */
export type ReleasedProjectContact = {
  memberUuid: string;
  /** 姓名快照；缺失说明该成员已离职。 */
  name?: string;
  /** 角色文案：主责 / 非主责。 */
  role?: string;
};

/** 409 响应体里的「历史已释放项目」条目。 */
export type ReleasedProjectInfo = {
  projectUuid: string;
  projectCode?: string;
  releasedAt?: string;
  releasedReason?: string;
  /** manual / auto_stale。 */
  source?: string;
  formerManagers: ReleasedProjectContact[];
};

/** 企业重复建项时后端返回的 409 结构化信息。 */
export type DuplicateProjectError = {
  status: number;
  /** 后端 message（含已有项目编码），取不到时给兜底文案。 */
  message: string;
  /** 已存在项目 uuid，用于「查看已有项目」。 */
  projectUuids: string[];
  /** 已存在项目编码（message 里解析出来的，或响应体直接给的）。 */
  projectCodes: string[];
  /**
   * 该企业历史**已释放**的项目及其原责任经理快照。
   * 后端未上线该字段时为空数组（向后兼容，不影响既有 4 个字段）。
   */
  releasedProjects: ReleasedProjectInfo[];
};

type ResponseErrorLike = {
  response?: {
    status?: number;
    data?: Record<string, unknown>;
  };
};

const PROJECT_CODE_PATTERN = /PRJ-\d{4}-\d+/g;

const toStringList = (value: unknown): string[] =>
  Array.isArray(value)
    ? value.filter((item): item is string => typeof item === "string" && !!item)
    : [];

/** 释放时间标准化到 `YYYY-MM-DD`（后端可能给 ISO 串或时间戳）。 */
const toReleasedDateText = (value: unknown): string | undefined => {
  if (typeof value !== "string" || !value.trim()) return undefined;
  const text = value.trim();
  const matched = text.match(/^(\d{4}-\d{2}-\d{2})/);
  if (matched) return matched[1];
  const parsed = new Date(text);
  if (Number.isNaN(parsed.getTime())) return text;
  return parsed.toISOString().slice(0, 10);
};

/**
 * 容错解析 `releasedProjects`。
 *
 * 后端字段尚未上线 / 字段名调整 / 类型不符时，一律返回空数组，**绝不抛错**，
 * 保证既有 409 处理路径（查看已有项目）不受影响。
 */
const parseReleasedProjects = (value: unknown): ReleasedProjectInfo[] => {
  if (!Array.isArray(value)) return [];
  const result: ReleasedProjectInfo[] = [];
  value.forEach((raw) => {
    if (!raw || typeof raw !== "object") return;
    const item = raw as Record<string, unknown>;
    const formerManagersRaw = item.formerManagers;
    const formerManagers: ReleasedProjectContact[] = Array.isArray(
      formerManagersRaw
    )
      ? formerManagersRaw
          .map((entry): ReleasedProjectContact | null => {
            if (!entry || typeof entry !== "object") return null;
            const contact = entry as Record<string, unknown>;
            const memberUuid =
              typeof contact.memberUuid === "string"
                ? contact.memberUuid.trim()
                : "";
            const name =
              typeof contact.name === "string" ? contact.name.trim() : "";
            const role =
              typeof contact.role === "string" ? contact.role.trim() : "";
            if (!memberUuid && !name) return null;
            return {
              memberUuid,
              ...(name ? { name } : {}),
              ...(role ? { role } : {}),
            };
          })
          .filter((entry): entry is ReleasedProjectContact => entry !== null)
      : [];
    const projectUuid =
      typeof item.projectUuid === "string" ? item.projectUuid.trim() : "";
    const projectCode =
      typeof item.projectCode === "string" ? item.projectCode.trim() : "";
    const releasedAt = toReleasedDateText(item.releasedAt);
    const releasedReason =
      typeof item.releasedReason === "string" ? item.releasedReason.trim() : "";
    const source = typeof item.source === "string" ? item.source.trim() : "";
    // 一条都识别不出来就跳过，避免把垃圾条目渲染到弹窗里。
    if (!projectUuid && !projectCode && !releasedAt && !formerManagers.length) {
      return;
    }
    result.push({
      projectUuid,
      ...(projectCode ? { projectCode } : {}),
      ...(releasedAt ? { releasedAt } : {}),
      ...(releasedReason ? { releasedReason } : {}),
      ...(source ? { source } : {}),
      formerManagers,
    });
  });
  return result;
};

/**
 * 解析 POST /bff/investment/projects 的 409（企业已存在正式项目）。
 *
 * 后端契约：status=409，message 形如
 * 「该企业已存在投资项目（项目编码 PRJ-2026-001），不允许重复创建」，
 * 并带 duplicateProjectUuids / duplicateProjectCodes。这里对字段名做宽松兜底，
 * 避免后端只改一个字段名就把「查看已有项目」入口弄丢。
 * 非 409 返回 null，调用方据此走普通错误分支。
 */
export const parseDuplicateProjectError = (
  error: unknown
): DuplicateProjectError | null => {
  const response = (error as ResponseErrorLike | null)?.response;
  if (!response || response.status !== 409) return null;
  const data = response.data ?? {};
  const rawMessage =
    data.message ?? data.errorMessage ?? data.error ?? data.msg ?? "";
  const message = Array.isArray(rawMessage)
    ? rawMessage.filter(Boolean).join("，")
    : typeof rawMessage === "string"
    ? rawMessage.trim()
    : "";
  const projectUuids = toStringList(
    data.duplicateProjectUuids ?? data.projectUuids
  );
  const projectCodes = toStringList(
    data.duplicateProjectCodes ?? data.projectCodes
  );
  // message 里的项目编码是后端唯一保证「一定存在」的信息：字段名可能改，编码不会改。
  const codesFromMessage = message.match(PROJECT_CODE_PATTERN) ?? [];
  return {
    status: 409,
    message: message || "该企业已存在投资项目，不允许重复创建",
    projectUuids,
    projectCodes: projectCodes.length ? projectCodes : codesFromMessage,
    releasedProjects: parseReleasedProjects(data.releasedProjects),
  };
};

/**
 * 原责任经理展示名：姓名快照优先；缺失时回退 memberUuid 前 8 位并标注「（成员已离职）」。
 */
export const formerManagerDisplayName = (
  contact: ReleasedProjectContact
): string => {
  const name = (contact.name ?? "").trim();
  if (name) return name;
  const uuid = (contact.memberUuid ?? "").trim();
  if (!uuid) return "（成员已离职）";
  return `${uuid.slice(0, 8)}（成员已离职）`;
};

/**
 * 原责任经理名单文案：主责在前、非主责在后，形如「张三（主责）、李四（非主责）」。
 * 没有可用名单时返回空串（调用方据此不渲染「联系」那段）。
 */
export const formerManagersText = (
  contacts: ReleasedProjectContact[] | undefined
): string => {
  const list = (contacts ?? []).filter(
    (item) => (item.name ?? "").trim() || (item.memberUuid ?? "").trim()
  );
  if (!list.length) return "";
  const rank = (role?: string) =>
    role === "主责" ? 0 : role === "非主责" ? 1 : 2;
  const sorted = [...list].sort((a, b) => rank(a.role) - rank(b.role));
  return sorted
    .map(
      (item) =>
        `${formerManagerDisplayName(item)}（${
          item.role?.trim() || "责任经理"
        }）`
    )
    .join("、");
};

/**
 * 取后端业务错误码（`code` / `errorCode` 两种历史写法都认）。
 * 建项入口要按错误码分流：`COMPANY_PROJECT_EXISTS`（同企业在办项目）与
 * `PROJECT_NAME_EXISTS`（项目名重复）都是 409，只看 HTTP 状态分不开，必须看码。
 */
export const getInvestmentErrorCode = (error: unknown): string | null => {
  const response = (error as ResponseErrorLike | null)?.response;
  const data = response?.data;
  if (!data) return null;
  const raw = data.code ?? data.errorCode ?? data.bizCode;
  if (typeof raw !== "string" || !raw.trim()) return null;
  return raw.trim().toUpperCase();
};

export const toCreateLeadBody = (data: Partial<Lead>) => {
  const body = {
    company: data.company,
    primaryOrganizationUuid: data.primaryOrganizationUuid,
    referrerPersonUuid: data.referrerPersonUuid,
    industry: data.industry,
    round: data.round,
    financingAmount: data.financingAmount,
    preMoneyValuation: data.preMoneyValuation,
    source: data.source,
    referrer: data.referrer,
    owner: data.owner,
    priority: data.priority,
    ownerMemberUuid: data.ownerMemberUuid,
    region: data.region,
    regionUuid: data.regionUuid,
    summary: data.summary,
    highlight: data.highlight,
    initialJudgment: data.initialJudgment,
    // 企业档案 / 储备信息（项目线索资源池表单）
    investmentType: data.investmentType,
    transactionType: data.transactionType,
    dealDetails: data.dealDetails,
    assetName: data.assetName,
    assetLocation: data.assetLocation,
    assetCategory: data.assetCategory,
    ownershipProof: data.ownershipProof,
    investmentMethod: data.investmentMethod,
    purchaser: data.purchaser,
    transferor: data.transferor,
    transferorLocation: data.transferorLocation,
    transferorRegistrationNumber: data.transferorRegistrationNumber,
    hasThirdPartyRights: data.hasThirdPartyRights,
    thirdPartyRightsDescription: data.thirdPartyRightsDescription,
    projectOverview: data.projectOverview,
    registrationLocation: data.registrationLocation,
    registrationStatus: data.registrationStatus,
    shortName: data.shortName,
    enterpriseType: data.enterpriseType,
    establishedDate: data.establishedDate,
    operatingTerm: data.operatingTerm,
    registeredCapital: data.registeredCapital,
    registeredCapitalUnit: data.registeredCapitalUnit,
    registeredCapitalCurrency: data.registeredCapitalCurrency,
    paidInCapital: data.paidInCapital,
    paidInCapitalUnit: data.paidInCapitalUnit,
    paidInCapitalCurrency: data.paidInCapitalCurrency,
    unifiedSocialCreditCode: data.unifiedSocialCreditCode,
    legalRepresentative: data.legalRepresentative,
    companyEnglishName: data.companyEnglishName,
    overseasLocation: data.overseasLocation,
    registrationNumber: data.registrationNumber,
    registrationAuthority: data.registrationAuthority,
    locationDetail: data.locationDetail,
    registeredAddress: data.registeredAddress,
    businessScope: data.businessScope,
    mainBusiness: data.mainBusiness,
    isStateOwned: data.isStateOwned,
    isListed: data.isListed,
    listingBoard: data.listingBoard,
    stockCode: data.stockCode,
    listingProgress: data.listingProgress,
    companyIntro: data.companyIntro,
    // 存 uuid、显示名字：提交只带 uuid（enterpriseTagUuids）。
    enterpriseTagUuids: data.enterpriseTagUuids,
    belongSection: data.belongSection,
    stateOwnedType: data.stateOwnedType,
    reserveType: data.reserveType,
    projectSource: data.projectSource,
    projectSourceDetail: data.projectSourceDetail,
    enterpriseIntro: data.enterpriseIntro,
    projectFeatures: data.projectFeatures,
    projectFeature: data.projectFeature,
    latestPerformance: data.latestPerformance,
    financingDemand: data.financingDemand,
    landingNeeds: data.landingNeeds,
    landingDemand: data.landingDemand,
    preliminaryJudgment: data.preliminaryJudgment,
    // 企业档案：新建企业时后端据此创建企业库记录，已存在时同步企业档案。
    org: data.org,
    shareholders: data.shareholders,
    outboundInvestments: data.outboundInvestments,
  };
  return Object.fromEntries(
    Object.entries(body).filter(([, value]) => value !== undefined)
  );
};

export const toUpdateLeadBody = (data: Partial<Lead>) => {
  return Object.fromEntries(
    Object.entries({
      name: data.name,
      company: data.company,
      primaryOrganizationUuid: data.primaryOrganizationUuid,
      referrerPersonUuid: data.referrerPersonUuid,
      industry: data.industry,
      round: data.round,
      financingAmount: data.financingAmount,
      preMoneyValuation: data.preMoneyValuation,
      source: data.source,
      referrer: data.referrer,
      owner: data.owner,
      ownerMemberUuid: data.ownerMemberUuid,
      priority: data.priority,
      region: data.region,
      regionUuid: data.regionUuid,
      summary: data.summary,
      nextAction: data.nextAction,
      highlight: data.highlight,
      initialJudgment: data.initialJudgment,
      investmentType: data.investmentType,
      transactionType: data.transactionType,
      dealDetails: data.dealDetails,
      assetName: data.assetName,
      assetLocation: data.assetLocation,
      assetCategory: data.assetCategory,
      ownershipProof: data.ownershipProof,
      investmentMethod: data.investmentMethod,
      purchaser: data.purchaser,
      transferor: data.transferor,
      transferorLocation: data.transferorLocation,
      transferorRegistrationNumber: data.transferorRegistrationNumber,
      hasThirdPartyRights: data.hasThirdPartyRights,
      thirdPartyRightsDescription: data.thirdPartyRightsDescription,
      projectOverview: data.projectOverview,
      registrationLocation: data.registrationLocation,
      registrationStatus: data.registrationStatus,
      shortName: data.shortName,
      enterpriseType: data.enterpriseType,
      establishedDate: data.establishedDate,
      operatingTerm: data.operatingTerm,
      registeredCapital: data.registeredCapital,
      registeredCapitalUnit: data.registeredCapitalUnit,
      registeredCapitalCurrency: data.registeredCapitalCurrency,
      paidInCapital: data.paidInCapital,
      paidInCapitalUnit: data.paidInCapitalUnit,
      paidInCapitalCurrency: data.paidInCapitalCurrency,
      unifiedSocialCreditCode: data.unifiedSocialCreditCode,
      legalRepresentative: data.legalRepresentative,
      companyEnglishName: data.companyEnglishName,
      overseasLocation: data.overseasLocation,
      registrationNumber: data.registrationNumber,
      registrationAuthority: data.registrationAuthority,
      locationDetail: data.locationDetail,
      registeredAddress: data.registeredAddress,
      businessScope: data.businessScope,
      mainBusiness: data.mainBusiness,
      isStateOwned: data.isStateOwned,
      isListed: data.isListed,
      listingBoard: data.listingBoard,
      stockCode: data.stockCode,
      listingProgress: data.listingProgress,
      companyIntro: data.companyIntro,
      enterpriseTagUuids: data.enterpriseTagUuids,
      belongSection: data.belongSection,
      stateOwnedType: data.stateOwnedType,
      reserveType: data.reserveType,
      projectSource: data.projectSource,
      projectSourceDetail: data.projectSourceDetail,
      enterpriseIntro: data.enterpriseIntro,
      projectFeatures: data.projectFeatures,
      projectFeature: data.projectFeature,
      latestPerformance: data.latestPerformance,
      financingDemand: data.financingDemand,
      landingNeeds: data.landingNeeds,
      landingDemand: data.landingDemand,
      preliminaryJudgment: data.preliminaryJudgment,
      // 企业档案：新建企业时后端据此创建企业库记录，已存在时同步企业档案。
      org: data.org,
      shareholders: data.shareholders,
      outboundInvestments: data.outboundInvestments,
    }).filter(([, value]) => value !== undefined)
  );
};

// ── 台账报表 / 驾驶舱 / 基础库 ──

export interface LedgerFundRow {
  id: string;
  /** @deprecated 后端已废弃基金代码；兼容旧响应。 */
  code?: string;
  name: string;
  type: string;
  organizationForm: string;
  manager: string;
  phase: string;
  controlType: string;
  ourShareRatio: number | null;
  targetAmount: number;
  committedAmount: number;
  paidAmount: number;
  unpaidAmount: number;
  registrationType: string;
  registrationNumber: string;
}
export interface LedgerFundReport {
  rows: LedgerFundRow[];
  totals: {
    fundCount: number;
    targetAmount: number;
    committedAmount: number;
    paidAmount: number;
    unpaidAmount: number;
  };
}
export interface LedgerProjectRow {
  id: string;
  name: string;
  company: string;
  industry: string;
  region: string;
  regionUuid?: string | null;
  stage: string;
  fundName: string;
  plannedAmount: number;
  investedAmount: number;
  holdingRatio: number;
  manager: string;
  closed: boolean;
}
export interface LedgerProjectReport {
  rows: LedgerProjectRow[];
  totals: {
    projectCount: number;
    closedCount: number;
    plannedAmount: number;
    investedAmount: number;
  };
}
export interface LedgerCockpit {
  overview: {
    fundCount: number;
    selfManagedFundCount: number;
    participatingFundCount: number;
    fundScale: number;
    ourCommittedAmount: number;
    ourPaidAmount: number;
    projectCount: number;
    closedProjectCount: number;
    plannedAmount: number;
    investedAmount: number;
    portfolioCompanyCount: number;
    portfolioCost: number;
    portfolioHolding: number;
  };
  fundTypeAnalysis: Array<{ label: string; value: number }>;
  fundPhaseAnalysis: Array<{ label: string; value: number }>;
  projectStageAnalysis: Array<{ label: string; value: number }>;
  projectRegionDistribution: Array<{ label: string; value: number }>;
  projectIndustryDistribution: Array<{ label: string; value: number }>;
  managementFeeByYear: Array<{ year: string; amount: number }>;
}
export interface ProjectPipelineRow {
  id: string;
  name: string;
  company: string;
  systemStage: string;
  alignedStage: string;
  manager: string;
}
export interface ProjectPipeline {
  rows: ProjectPipelineRow[];
  counts: Array<{ label: string; value: number }>;
}
export interface InvestorLibrary {
  rows: Array<{
    id: string;
    name: string;
    shortName: string;
    industry: string;
    region: string;
    regionUuid?: string | null;
    creditCode: string;
    committedAmount: number;
    paidAmount: number;
    fundCount: number;
  }>;
  totals: { count: number; committedAmount: number; paidAmount: number };
}
export interface CompanyLibrary {
  rows: Array<{
    id: string;
    name: string;
    shortName: string;
    industry: string;
    region: string;
    regionUuid?: string | null;
    creditCode: string;
    isPortfolio: boolean;
    portfolioCompanyId: string | null;
    projectCount: number;
  }>;
  totals: { count: number; portfolioCount: number };
}
export interface ManagerLibrary {
  rows: Array<{
    id: string;
    name: string;
    shortName: string;
    creditCode: string;
    fundCount: number;
  }>;
  totals: { count: number };
}
export interface TagLibrary {
  industries: Array<{ label: string; value: number }>;
  regions: Array<{ label: string; value: number }>;
  orgTypes: Array<{ label: string; value: number }>;
}

export const investmentApi = {
  listFunds: () => request<Fund[]>("/bff/investment/funds"),

  /** 新建基金，未指定阶段时统一从募集期开始。 */
  createFund: (data: Pick<Fund, "name" | "currency">) =>
    request<Fund>("/bff/investment/funds", {
      method: "POST",
      data,
    }),

  updateFund: (uuid: string, data: UpdateFundPayload) =>
    request<Fund>(`/bff/investment/funds/${uuid}`, {
      method: "PATCH",
      data,
    }),

  listApprovals: (params: {
    bizType: string;
    bizUuid: string;
    investmentBatchUuid?: string;
  }) =>
    request<InvestmentApproval[]>("/bff/investment/governance/approvals", {
      params,
    }),

  createApproval: (data: {
    approvalType: InvestmentApprovalType;
    bizType: string;
    bizUuid: string;
    investmentBatchUuid?: string;
  }) =>
    request<InvestmentApproval>("/bff/investment/governance/approvals", {
      method: "POST",
      data,
    }),

  completeApproval: (
    uuid: string,
    data: {
      status: Extract<InvestmentApprovalStatus, "已通过" | "已驳回">;
      opinion?: string;
      adminExceptionReason?: string;
    }
  ) =>
    request<InvestmentApproval>(
      `/bff/investment/governance/approvals/${uuid}/complete`,
      {
        method: "PATCH",
        data,
      }
    ),

  withdrawApproval: (uuid: string) =>
    request<InvestmentApproval>(
      `/bff/investment/governance/approvals/${uuid}/withdraw`,
      {
        method: "POST",
      }
    ),

  syncWorkflowApproval: (uuid: string) =>
    request<InvestmentApproval>(
      `/bff/investment/governance/approvals/${uuid}/workflow-sync`,
      { method: "POST" }
    ),

  getDecisionWorkflowLaunch: (uuid: string) =>
    request<DecisionWorkflowLaunchStatus>(
      `/bff/investment/governance/approvals/${uuid}/workflow-launch`
    ),

  retryWorkflowApproval: (uuid: string) =>
    request<InvestmentApproval>(
      `/bff/investment/governance/approvals/${uuid}/workflow-retry`,
      { method: "POST" }
    ),

  /** 重新提交被审批中心退回发起人的审批：后端会先按当前依据重新冻结，再完成发起人首节点待办。 */
  resubmitWorkflowApproval: (uuid: string, data?: { comment?: string }) =>
    request<InvestmentApproval>(
      `/bff/investment/governance/approvals/${uuid}/workflow-resubmit`,
      { method: "POST", data: data ?? {} }
    ),

  listWorkflowTodo: (params?: { page?: number; limit?: number }) =>
    request<{ data: InvestmentWorkflowTask[]; total: number }>(
      "/bff/investment/governance/workflow/todo",
      {
        params,
      }
    ),

  listWorkflowDone: (params?: { page?: number; limit?: number }) =>
    request<{ data: InvestmentWorkflowTask[]; total: number }>(
      "/bff/investment/governance/workflow/done",
      {
        params,
      }
    ),

  performWorkflowTask: (
    taskId: string,
    data: {
      actionKey: string;
      comment?: string;
    }
  ) =>
    request<InvestmentWorkflowTask>(
      `/bff/investment/governance/workflow/tasks/${taskId}/action`,
      { method: "POST", data }
    ),

  getWorkflowTask: (taskId: string) =>
    request<{
      task: InvestmentWorkflowTask;
      detail: unknown;
      timeline: unknown[];
    }>(`/bff/investment/governance/workflow/tasks/${taskId}`),

  getWorkflowConfig: () =>
    request<InvestmentWorkflowConfig | null>(
      "/bff/investment/governance/workflow/config"
    ),

  listWorkflowTemplates: () =>
    request<InvestmentWorkflowTemplate[]>(
      "/bff/investment/governance/workflow/templates"
    ),

  updateWorkflowConfig: (data: InvestmentWorkflowConfig) =>
    request<InvestmentWorkflowConfig>(
      "/bff/investment/governance/workflow/config",
      {
        method: "PUT",
        data,
      }
    ),

  listMaterials: (params: { bizType: string; bizUuid: string }) =>
    request<InvestmentMaterialItem[]>("/bff/investment/governance/materials", {
      params,
    }),

  createMaterial: (
    bizType: string,
    bizUuid: string,
    data: { materialCode: string; title: string; required?: boolean }
  ) =>
    request<InvestmentMaterialItem>(
      `/bff/investment/governance/materials/${bizType}/${bizUuid}`,
      { method: "POST", data }
    ),

  verifyMaterial: (
    uuid: string,
    data: { verified?: boolean; waiverReason?: string }
  ) =>
    request<InvestmentMaterialItem>(
      `/bff/investment/governance/materials/${uuid}/verify`,
      {
        method: "PATCH",
        data,
      }
    ),

  linkMaterialAttachment: (uuid: string, attachmentUuid: string) =>
    request<{ uuid: string }>(
      `/bff/investment/governance/materials/${uuid}/attachments`,
      {
        method: "POST",
        data: { attachmentUuid },
      }
    ),

  listLeads: (params?: LeadListQuery) =>
    request<LeadListResult>("/bff/investment/leads", { params }),

  getLead: (uuid: string) => request<Lead>(`/bff/investment/leads/${uuid}`),

  listLeadOwnershipHistory: (uuid: string) =>
    request<LeadOwnershipEvent[]>(
      `/bff/investment/leads/${uuid}/ownership-history`
    ),

  createLead: (data: Partial<Lead>) =>
    request<Lead>("/bff/investment/leads", {
      method: "POST",
      data: toCreateLeadBody(data),
    }),

  assignLeads: (leadUuids: string[], ownerMemberUuid: string, owner?: string) =>
    request<{
      assigned: number;
      skipped: number;
      results: Array<{
        leadId: string;
        status: "assigned" | "skipped";
        message?: string;
      }>;
    }>("/bff/investment/leads/assign", {
      method: "POST",
      data: { leadUuids, ownerMemberUuid, owner },
    }),

  updateLead: (uuid: string, data: Partial<Lead>) =>
    request<Lead>(`/bff/investment/leads/${uuid}`, {
      method: "PATCH",
      data: toUpdateLeadBody(data),
    }),

  updateLeadStatus: (
    uuid: string,
    status: LeadStatus,
    closure?: { rejectionReason: LeadScreeningRejectionReason; note?: string }
  ) =>
    request<Lead>(`/bff/investment/leads/${uuid}/status`, {
      method: "PATCH",
      data: { status, ...closure },
    }),

  resumeLead: (uuid: string) =>
    request<Lead>(`/bff/investment/leads/${uuid}/resume`, { method: "POST" }),

  deleteLead: (uuid: string) =>
    request<{ message: string }>(`/bff/investment/leads/${uuid}`, {
      method: "DELETE",
    }),

  listLeadFollowUps: (uuid: string) =>
    request<FollowUp[]>(`/bff/investment/leads/${uuid}/follow-ups`),

  createLeadFollowUp: (
    uuid: string,
    data: {
      details?: FollowUpDetails;
      content: string;
      nextAction?: string;
      dueOn?: string;
      /**
       * 本次跟进自带的附件。后端存 `inv_lead_follow_up.attachment_uuid`
       * 且**做归属核验**（附件的 bizType+bizUuid 必须等于本线索）→
       * 必须先上传拿 uuid、再随本接口提交（`@/shared/attachment` 的 AttachmentField 语义）。
       */
      attachmentUuid?: string;
      completeCurrentAction?: boolean;
      expectedCurrentAction?: string;
    }
  ) =>
    request<FollowUp>(`/bff/investment/leads/${uuid}/follow-ups`, {
      method: "POST",
      data,
    }),

  listLeadScreenings: (uuid: string) =>
    request<LeadScreening[]>(`/bff/investment/leads/${uuid}/screenings`),

  createLeadScreening: (
    uuid: string,
    data: {
      opinion: LeadScreeningOpinion;
      rejectionReason?: LeadScreeningRejectionReason;
      note?: string;
    }
  ) =>
    request<LeadScreening>(`/bff/investment/leads/${uuid}/screenings`, {
      method: "POST",
      data,
    }),

  convertLead: (
    uuid: string,
    input?:
      | string
      | {
          fundUuid?: string;
          projectName?: string;
          targetRound?: string;
          targetInvestmentAmount?: number;
          /** 入库申请项目信息（对齐参考平台） */
          managementPlatformUuid?: string;
          targetRatio?: number;
          investmentMethod?: string;
          investmentRole?: string;
          planInvestDate?: string;
          hasBoardSeat?: boolean;
        }
  ) =>
    request<InvestmentProject>(`/bff/investment/leads/${uuid}/convert`, {
      method: "POST",
      headers: { "Idempotency-Key": `lead:${uuid}:convert` },
      data: typeof input === "string" ? { fundUuid: input } : input,
    }).then(withProjectFundSnapshot),

  applyLeadConversion: (
    uuid: string,
    data: {
      targetFundUuid?: string;
      targetInvestmentAmount?: number;
      targetRound?: string;
      applyReason?: string;
      /** 入库申请项目信息（对齐参考平台） */
      managementPlatformUuid?: string;
      targetRatio?: number;
      investmentMethod?: string;
      investmentRole?: string;
      planInvestDate?: string;
      hasBoardSeat?: boolean;
    }
  ) =>
    request<Lead>(`/bff/investment/leads/${uuid}/apply-conversion`, {
      method: "POST",
      data,
    }),

  rejectLeadConversion: (uuid: string, data: { rejectReason: string }) =>
    request<Lead>(`/bff/investment/leads/${uuid}/reject-conversion`, {
      method: "POST",
      data,
    }),

  approveLeadConversion: (uuid: string) =>
    request<InvestmentProject>(
      `/bff/investment/leads/${uuid}/approve-conversion`,
      {
        method: "POST",
        headers: { "Idempotency-Key": `lead:${uuid}:approve-conversion` },
      }
    ).then(withProjectFundSnapshot),

  listProjectsPage: (
    params: ProjectListQuery & { page: number; pageSize: number }
  ) =>
    request<{
      items: InvestmentProject[];
      total: number;
      page: number;
      pageSize: number;
    }>("/bff/investment/projects", { params }).then((result) => ({
      ...result,
      items: result.items.map(withProjectFundSnapshot),
    })),

  listProjects: (params?: ProjectListQuery) =>
    request<InvestmentProject[]>("/bff/investment/projects", { params }).then(
      (items) => (items ?? []).map(withProjectFundSnapshot)
    ),

  /**
   * 按企业反查项目：新建向导第一步用它**预检**「该企业是否已有在办项目」，
   * 避免用户填完第二步才被 409 拦下。
   *
   * `companyOrganizationUuid` 是本期新增的查询参数（见
   * docs/drafts/20261029-project-create-implementation.md §4.1 三情形）；
   * 在服务端支持该参数之前，这里再做一次客户端过滤兜底——
   * 否则老后端会忽略未知参数、返回全量项目，把「别的企业的项目」也算进预检里。
   */
  listProjectsByCompany: (
    companyOrganizationUuid: string,
    params?: ProjectListQuery
  ) =>
    request<InvestmentProject[]>("/bff/investment/projects", {
      params: { lifecycleView: "all", ...params, companyOrganizationUuid },
    }).then((items) =>
      (items ?? [])
        .map(withProjectFundSnapshot)
        .filter(
          (project) =>
            !companyOrganizationUuid ||
            project.companyOrganizationUuid === companyOrganizationUuid
        )
    ),

  /**
   * 新建投资项目（POST /bff/investment/projects）。
   *
   * 后端自动生成项目编码（PRJ-YYYY-XXX）；同一企业同轮次已有项目且未说明独立建项原因时返回 409，
   * 响应中附 duplicateProjectUuids，供调用方引导打开已有项目。
   * 这里刻意**不带** skipErrorHandler：409 由全局拦截器先弹一次 toast，
   * 调用方再用 parseDuplicateProjectError 弹带「查看已有项目」按钮的 Modal.error。
   */
  createProject: (data: CreateProjectPayload) =>
    request<InvestmentProject>("/bff/investment/projects", {
      method: "POST",
      data: toCreateProjectBody(data),
    }).then(withProjectFundSnapshot),

  /**
   * 投资项目列表「新建」：先选资源池企业，再按入库申请表单直接立项。
   *
   * 与「资源池入库」同一条后端链路（线索 → 入库快照 → 项目），只是入口权限是
   * investment-projects:create；同企业重复建项仍返回 409（由 parseDuplicateProjectError 处理）。
   */
  createProjectFromLead: (leadUuid: string, data: LeadStorageInput) =>
    request<InvestmentProject>("/bff/investment/projects/from-lead", {
      method: "POST",
      headers: { "Idempotency-Key": `lead:${leadUuid}:project-create` },
      data: { ...data, leadUuid },
    }).then(withProjectFundSnapshot),

  getProject: (uuid: string) =>
    request<InvestmentProject>(`/bff/investment/projects/${uuid}`).then(
      withProjectFundSnapshot
    ),

  getProjectChecklist: (uuid: string) =>
    request<ProjectChecklist>(`/bff/investment/projects/${uuid}/checklist`),

  /** 推进前预演：返回当前转移边上的缺项，前端据此拦截并展示，避免再维护一套前端卡口。 */
  getAdvanceGuard: (uuid: string, targetStage: string) =>
    request<ProjectChecklistAdvanceGuard>(
      `/bff/investment/projects/${uuid}/checklist/advance-guard`,
      { params: { targetStage } }
    ),

  getFundBatchAdvanceGuard: (
    uuid: string,
    fundBatchUuid: string,
    targetStage: string
  ) =>
    request<ProjectChecklistAdvanceGuard>(
      `/bff/investment/projects/${uuid}/fund-batches/${fundBatchUuid}/checklist/advance-guard`,
      { params: { targetStage } }
    ),

  getProjectChecklistOverview: () =>
    request<ProjectChecklistOverview>(
      "/bff/investment/admin/checklist/overview"
    ),

  getCompliancePledges: (uuid: string) =>
    request<ProjectCompliancePledgeResult>(
      `/bff/investment/projects/${uuid}/checklist/compliance-pledges`
    ),

  getCurrentQuestionnaire: (uuid: string) =>
    request<ProjectQuestionnaire | null>(
      `/bff/investment/projects/${uuid}/checklist/questionnaires/current`
    ),
  startQuestionnaire: (
    uuid: string,
    data?: { templateKind?: "medical" | "non_medical"; memberUuids?: string[] }
  ) =>
    request<ProjectQuestionnaire>(
      `/bff/investment/projects/${uuid}/checklist/questionnaires/start`,
      { method: "POST", data }
    ),
  submitQuestionnaire: (
    uuid: string,
    data: {
      questionnaireUuid: string;
      answers: Record<string, "A" | "B" | "C">;
      memberName?: string;
    }
  ) =>
    request<ProjectQuestionnaire>(
      `/bff/investment/projects/${uuid}/checklist/questionnaires/responses`,
      { method: "POST", data }
    ),

  syncDiligenceContract: (
    uuid: string,
    data: { attachmentUuid: string; approvalRef: string }
  ) =>
    request<{ configured: boolean; state?: ProjectChecklistRequirement }>(
      `/bff/investment/projects/${uuid}/checklist/diligence-contract/sync`,
      { method: "POST", data }
    ),

  signCompliancePledge: (uuid: string, memberUuid: string) =>
    request<ProjectCompliancePledge>(
      `/bff/investment/projects/${uuid}/checklist/compliance-pledges/${memberUuid}/sign`,
      { method: "POST" }
    ),

  getStageWaivers: (uuid: string) =>
    request<ProjectStageWaiver[]>(
      `/bff/investment/projects/${uuid}/checklist/waivers`
    ),

  applyStageWaiver: (
    uuid: string,
    requirementCodes: string[],
    reason: string
  ) =>
    request<ProjectStageWaiver[]>(
      `/bff/investment/projects/${uuid}/checklist/waive-apply`,
      { method: "POST", data: { requirementCodes, reason } }
    ),

  reviewStageWaiver: (
    waiverUuid: string,
    status: "approved" | "rejected",
    opinion: string
  ) =>
    request<ProjectStageWaiver>(
      `/bff/investment/checklist/waivers/${waiverUuid}/review`,
      { method: "PATCH", data: { status, opinion } }
    ),

  getCoInvestmentRatios: (uuid: string, fundBatchUuid?: string) =>
    request<ProjectCoInvestmentRatioResult>(
      `/bff/investment/projects/${uuid}/checklist/co-investment-ratios`,
      { params: fundBatchUuid ? { fundBatchUuid } : undefined }
    ),

  saveCoInvestmentRatios: (
    uuid: string,
    data: {
      fundBatchUuid?: string;
      ratios: Array<{ memberUuid: string; ratio: number }>;
    }
  ) =>
    request<ProjectCoInvestmentRatioResult>(
      `/bff/investment/projects/${uuid}/checklist/co-investment-ratios`,
      { method: "PUT", data }
    ),

  importForecastModel: (uuid: string, file: File) => {
    const formData = new FormData();
    formData.append("file", file);
    return request<{
      uuid: string;
      attachmentUuid: string;
      fileName: string;
    }>(`/bff/investment/projects/${uuid}/checklist/forecast-model/import`, {
      method: "POST",
      data: formData,
    });
  },

  downloadForecastModelTemplate: (uuid: string) =>
    request<Blob>(
      `/bff/investment/projects/${uuid}/checklist/forecast-model/template`,
      { responseType: "blob" }
    ),

  generateFilingResolution: (uuid: string, fundBatchUuid?: string) =>
    request<{ uuid: string; attachmentUuid: string }>(
      `/bff/investment/projects/${uuid}/checklist/filing-resolution/generate`,
      { method: "POST", data: fundBatchUuid ? { fundBatchUuid } : {} }
    ),

  listProjectFundBatches: (uuid: string) =>
    request<InvestmentProject["fundBatches"]>(
      `/bff/investment/projects/${uuid}/fund-batches`
    ),

  createProjectFundBatch: (
    uuid: string,
    data: {
      fundUuid: string;
      investmentAmount: number;
      holdingRatio: number;
      investmentMethod?: string;
      investmentRole?: string;
      plannedInvestAt?: string;
      tags?: string[];
      previousBatchUuid?: string;
    }
  ) =>
    request<NonNullable<InvestmentProject["fundBatches"]>[number]>(
      `/bff/investment/projects/${uuid}/fund-batches`,
      { method: "POST", data }
    ),

  updateProjectFundBatch: (
    uuid: string,
    batchUuid: string,
    data: {
      investmentAmount?: number;
      holdingRatio?: number;
      investmentMethod?: string | null;
      investmentRole?: string | null;
      plannedInvestAt?: string | null;
      confirmReconciliation?: boolean;
      tags?: string[];
    }
  ) =>
    request<NonNullable<InvestmentProject["fundBatches"]>[number]>(
      `/bff/investment/projects/${uuid}/fund-batches/${batchUuid}`,
      { method: "PATCH", data }
    ),

  terminateProjectFundBatch: (
    uuid: string,
    batchUuid: string,
    reason: string
  ) =>
    request<NonNullable<InvestmentProject["fundBatches"]>[number]>(
      `/bff/investment/projects/${uuid}/fund-batches/${batchUuid}/terminate`,
      { method: "POST", data: { reason } }
    ),

  updateProjectFundBatchStage: (
    uuid: string,
    batchUuid: string,
    stage: ProjectStage,
    expectedStage: ProjectStage
  ) =>
    request<NonNullable<InvestmentProject["fundBatches"]>[number]>(
      `/bff/investment/projects/${uuid}/fund-batches/${batchUuid}/stage`,
      { method: "PATCH", data: { stage, expectedStage } }
    ),

  listProjectTransferHistory: (uuid: string) =>
    request<ProjectTransferEvent[]>(
      `/bff/investment/projects/${uuid}/transfer-history`
    ),

  listProjectChanges: (uuid: string) =>
    request<ProjectChangeEvent[]>(`/bff/investment/projects/${uuid}/changes`),
  terminateProject: (uuid: string, reason: string) =>
    request<InvestmentProject>(`/bff/investment/projects/${uuid}/terminate`, {
      method: "POST",
      data: { reason },
    }),

  listProjectFollowUps: (uuid: string) =>
    request<ProjectFollowUp[]>(`/bff/investment/projects/${uuid}/follow-ups`),

  createProjectFollowUp: (
    uuid: string,
    data: {
      details?: FollowUpDetails;
      content: string;
      nextAction?: string;
      dueOn?: string;
      /** 跟进类型：线下跟进 / 线上沟通。 */
      trackType?: string;
      /** 跟进日期 YYYY-MM-DD。 */
      trackDate?: string;
      /** 参与人显示名。 */
      participants?: string;
      /**
       * 本次跟进自带的附件。后端存 `inv_project_follow_up.attachment_uuid`
       * 且与线索跟进一样**做归属核验**（附件 bizType+bizUuid 必须等于本项目）→
       * 必须先上传拿 uuid、再随本接口提交（`@/shared/attachment` 的 AttachmentField）。
       */
      attachmentUuid?: string;
      completeCurrentAction?: boolean;
      expectedCurrentAction?: string;
    }
  ) =>
    request<ProjectFollowUp>(`/bff/investment/projects/${uuid}/follow-ups`, {
      method: "POST",
      data,
    }),

  getDecisionReadiness: (uuid: string) =>
    request<LifecycleReadiness>(
      `/bff/investment/projects/${uuid}/decision-readiness`
    ),

  updateProject: (uuid: string, data: UpdateProjectPayload) =>
    request<InvestmentProject>(`/bff/investment/projects/${uuid}`, {
      method: "PATCH",
      data: toUpdateProjectBody(data),
    }).then(withProjectFundSnapshot),

  transferProject: (uuid: string, managerMemberUuid: string, reason: string) =>
    request<InvestmentProject>(`/bff/investment/projects/${uuid}/transfer`, {
      method: "POST",
      data: { managerMemberUuid, reason },
    }).then(withProjectFundSnapshot),

  updateProjectStage: (
    uuid: string,
    stage: ProjectStage,
    expectedStage: ProjectStage,
    reason?: string
  ) =>
    request<InvestmentProject>(`/bff/investment/projects/${uuid}/stage`, {
      method: "PATCH",
      data: {
        stage,
        expectedStage,
        ...(reason?.trim() ? { reason: reason.trim() } : {}),
      },
    }).then(withProjectFundSnapshot),

  resumeProject: (uuid: string) =>
    request<InvestmentProject>(`/bff/investment/projects/${uuid}/resume`, {
      method: "POST",
    }).then(withProjectFundSnapshot),

  submitDecision: (projectUuid: string, data?: SubmitDecisionPayload) =>
    request<InvestmentDecision>(
      `/bff/investment/projects/${projectUuid}/submit-decision`,
      { method: "POST", data: data ?? {} }
    ).then(withDecisionFundSnapshot),

  setDecisionResult: (decisionUuid: string, data: SetDecisionResultPayload) =>
    request<InvestmentDecision>(
      `/bff/investment/decisions/${decisionUuid}/result`,
      {
        method: "PATCH",
        data,
      }
    ).then(withDecisionFundSnapshot),

  listDecisions: (params?: DecisionListQuery) =>
    request<InvestmentDecision[]>("/bff/investment/decisions", { params }).then(
      (items) => (items ?? []).map(withDecisionFundSnapshot)
    ),

  listTransactions: (params?: TransactionListQuery) =>
    request<Transaction[]>("/bff/investment/transactions", { params }).then(
      (items) => (items ?? []).map(withTransactionFundSnapshot)
    ),

  createTransaction: (projectUuid: string) =>
    request<Transaction>("/bff/investment/transactions", {
      method: "POST",
      data: { projectUuid },
    }).then(withTransactionFundSnapshot),

  getTransaction: (uuid: string) =>
    request<Transaction>(`/bff/investment/transactions/${uuid}`).then(
      withTransactionFundSnapshot
    ),

  getClosingReadiness: (uuid: string) =>
    request<LifecycleReadiness>(
      `/bff/investment/transactions/${uuid}/closing-readiness`
    ),

  completeClosing: (
    uuid: string,
    payload?: {
      finalInvestmentAmount?: number;
      cashWaiver?: { reason: string };
    }
  ) =>
    request<{
      transaction: Transaction;
      handoff?: {
        companyUuid?: string;
        investmentRelationUuid?: string;
        companyName?: string;
      };
    }>(`/bff/investment/transactions/${uuid}/complete-closing`, {
      method: "POST",
      data: payload ?? {},
    }).then((result) => ({
      ...result,
      transaction: withTransactionFundSnapshot(result.transaction),
    })),

  getDashboard: (params?: {
    manager?: string;
    industry?: string;
    source?: string;
    fund?: string;
  }) => request<InvestmentDashboard>("/bff/investment/dashboard", { params }),

  getDashboardMyWork: () =>
    request<DashboardMyWorkItem[]>("/bff/investment/dashboard/my-work"),

  updateTransactionProgress: (
    uuid: string,
    data: UpdateTransactionProgressPayload
  ) =>
    request<Transaction>(`/bff/investment/transactions/${uuid}/progress`, {
      method: "PATCH",
      data,
    }).then(withTransactionFundSnapshot),

  listDiligenceItems: (projectUuid: string) =>
    request<DueDiligenceItem[]>(
      `/bff/investment/projects/${projectUuid}/diligence-items`
    ),

  createDiligenceItem: (
    projectUuid: string,
    data: Omit<DueDiligenceItem, "id" | "projectId">
  ) =>
    request<DueDiligenceItem>(
      `/bff/investment/projects/${projectUuid}/diligence-items`,
      { method: "POST", data }
    ),

  updateDiligenceItem: (uuid: string, data: Partial<DueDiligenceItem>) =>
    request<DueDiligenceItem>(`/bff/investment/diligence-items/${uuid}`, {
      method: "PATCH",
      data,
    }),

  listProjectRiskItems: (projectUuid: string) =>
    request<RiskItem[]>(`/bff/investment/projects/${projectUuid}/risk-items`),

  createProjectRiskItem: (
    projectUuid: string,
    data: Omit<RiskItem, "id" | "projectId">
  ) =>
    request<RiskItem>(`/bff/investment/projects/${projectUuid}/risk-items`, {
      method: "POST",
      data,
    }),

  updateProjectRiskItem: (uuid: string, data: Partial<RiskItem>) =>
    request<RiskItem>(`/bff/investment/risk-items/${uuid}`, {
      method: "PATCH",
      data,
    }),

  listTransactionAmendments: (transactionUuid: string) =>
    request<TransactionAmendment[]>(
      `/bff/investment/transactions/${transactionUuid}/amendments`
    ),

  createTransactionAmendment: (
    transactionUuid: string,
    data: {
      type: TransactionAmendment["type"];
      title: string;
      summary?: string;
      signedAt?: string;
      owner?: string;
      affectsAmount?: boolean;
      affectsHolding?: boolean;
      amountAfter?: number;
      holdingAfter?: number;
      /** 关联主协议 UUID（须属于同一交易）。 */
      agreementUuid?: string;
      /** 生效后回写主协议的字段补丁。 */
      agreementPatch?: TransactionAgreementPatch;
    }
  ) =>
    request<TransactionAmendment>(
      `/bff/investment/transactions/${transactionUuid}/amendments`,
      { method: "POST", data }
    ),

  updateTransactionAmendment: (
    uuid: string,
    data: Partial<TransactionAmendment>
  ) =>
    request<TransactionAmendment>(
      `/bff/investment/transaction-amendments/${uuid}`,
      { method: "PATCH", data }
    ),

  listProjectFilings: (projectUuid: string) =>
    request<ProjectFiling[]>(`/bff/investment/projects/${projectUuid}/filings`),
  createProjectFiling: (
    projectUuid: string,
    data: Pick<
      ProjectFiling,
      | "itemName"
      | "department"
      | "investmentSubjectUuid"
      | "investmentSubjectName"
      | "investmentAmountRmb"
      | "currency"
      | "preMoneyValuation"
      | "postMoneyValuation"
      | "projectOverview"
      | "projectAdvantages"
      | "existingProblems"
      | "reviewMatter"
      | "investmentReason"
      | "investmentAmount"
      | "holdingRatio"
      | "progress"
    >
  ) =>
    request<ProjectFiling>(`/bff/investment/projects/${projectUuid}/filings`, {
      method: "POST",
      data,
    }),
  updateProjectFiling: (uuid: string, data: Partial<ProjectFiling>) =>
    request<ProjectFiling>(`/bff/investment/project-filings/${uuid}`, {
      method: "PATCH",
      data,
    }),

  // ── 国资备案（可选阶段） ──
  listStateAssetFilings: (projectUuid: string) =>
    request<StateAssetFiling[]>(
      `/bff/investment/projects/${projectUuid}/state-asset-filings`
    ),
  createStateAssetFiling: (projectUuid: string, data: StateAssetFilingInput) =>
    request<StateAssetFiling>(
      `/bff/investment/projects/${projectUuid}/state-asset-filings`,
      { method: "POST", data }
    ),
  updateStateAssetFiling: (
    projectUuid: string,
    filingUuid: string,
    data: Partial<StateAssetFilingInput>
  ) =>
    request<StateAssetFiling>(
      `/bff/investment/projects/${projectUuid}/state-asset-filings/${filingUuid}`,
      { method: "PATCH", data }
    ),

  listDiligenceReports: (projectUuid: string) =>
    request<DiligenceReport[]>(
      `/bff/investment/projects/${projectUuid}/diligence-reports`
    ),
  createDiligenceReport: (
    projectUuid: string,
    data: Partial<DiligenceReport>
  ) =>
    request<DiligenceReport>(
      `/bff/investment/projects/${projectUuid}/diligence-reports`,
      { method: "POST", data }
    ),
  updateDiligenceReport: (uuid: string, data: Partial<DiligenceReport>) =>
    request<DiligenceReport>(`/bff/investment/diligence-reports/${uuid}`, {
      method: "PATCH",
      data,
    }),

  listProjectRiskReviews: (projectUuid: string) =>
    request<ProjectRiskReview[]>(
      `/bff/investment/projects/${projectUuid}/risk-reviews`
    ),
  createProjectRiskReview: (
    projectUuid: string,
    data: CreateProjectRiskReviewInput
  ) =>
    request<ProjectRiskReview>(
      `/bff/investment/projects/${projectUuid}/risk-reviews`,
      { method: "POST", data }
    ),
  updateProjectRiskReview: (uuid: string, data: UpdateProjectRiskReviewInput) =>
    request<ProjectRiskReview>(`/bff/investment/project-risk-reviews/${uuid}`, {
      method: "PATCH",
      data,
    }),

  listProjectPreMeetings: (projectUuid: string) =>
    request<PreMeetingResponse[]>(
      `/bff/investment/projects/${projectUuid}/pre-meetings`
    ).then((records) => records.map(withPreMeeting)),
  saveProjectPreMeeting: (
    projectUuid: string,
    data: Omit<
      ProjectPreMeeting,
      "id" | "projectId" | "version" | "createdAt"
    > & { expectedVersion?: number },
    meetingUuid?: string
  ) =>
    request<PreMeetingResponse>(
      meetingUuid
        ? `/bff/investment/pre-meetings/${meetingUuid}`
        : `/bff/investment/projects/${projectUuid}/pre-meetings`,
      {
        method: meetingUuid ? "PATCH" : "POST",
        data: {
          title: data.meetingName,
          attachmentUuid: data.attachmentUuid,
          attachmentUuids: data.attachmentUuids,
          meetingType: data.meetingType,
          meetingFormat: data.meetingFormat,
          meetingDate: data.meetingDate,
          meetingEndAt: data.meetingEndAt,
          attendeeMemberUuids: data.attendees,
          content: data.content,
          result: data.result,
          expectedVersion: data.expectedVersion,
        },
      }
    ).then(withPreMeeting),
  deleteProjectPreMeeting: (
    _projectUuid: string,
    meetingUuid: string,
    expectedVersion: number
  ) =>
    request<{ success: boolean }>(
      `/bff/investment/pre-meetings/${meetingUuid}`,
      { method: "DELETE", data: { expectedVersion } }
    ),

  listTransactionAgreements: (transactionUuid: string) =>
    request<TransactionAgreement[]>(
      `/bff/investment/transactions/${transactionUuid}/agreements`
    ),
  createTransactionAgreement: (
    transactionUuid: string,
    data: Partial<TransactionAgreement>
  ) =>
    request<TransactionAgreement>(
      `/bff/investment/transactions/${transactionUuid}/agreements`,
      { method: "POST", data }
    ),
  updateTransactionAgreement: (
    uuid: string,
    data: Partial<TransactionAgreement>
  ) =>
    request<TransactionAgreement>(
      `/bff/investment/transaction-agreements/${uuid}`,
      { method: "PATCH", data }
    ),

  listTransactionPayments: (transactionUuid: string) =>
    request<TransactionPayment[]>(
      `/bff/investment/transactions/${transactionUuid}/payments`
    ),
  createTransactionPayment: (
    transactionUuid: string,
    data: Partial<TransactionPayment>
  ) =>
    request<TransactionPayment>(
      `/bff/investment/transactions/${transactionUuid}/payments`,
      { method: "POST", data }
    ),
  updateTransactionPayment: (uuid: string, data: Partial<TransactionPayment>) =>
    request<TransactionPayment>(
      `/bff/investment/transaction-payments/${uuid}`,
      { method: "PATCH", data }
    ),

  listClosingConditions: (transactionUuid: string) =>
    request<ClosingCondition[]>(
      `/bff/investment/transactions/${transactionUuid}/closing-conditions`
    ),
  createClosingCondition: (
    transactionUuid: string,
    data: Partial<ClosingCondition>
  ) =>
    request<ClosingCondition>(
      `/bff/investment/transactions/${transactionUuid}/closing-conditions`,
      { method: "POST", data }
    ),
  updateClosingCondition: (uuid: string, data: Partial<ClosingCondition>) =>
    request<ClosingCondition>(`/bff/investment/closing-conditions/${uuid}`, {
      method: "PATCH",
      data,
    }),

  // ── 台账报表 / 驾驶舱 / 基础库 ──
  getFundLedger: (controlType?: string) =>
    request<LedgerFundReport>("/bff/investment/reports/funds", {
      params: controlType ? { controlType } : undefined,
    }),

  getProjectLedger: () =>
    request<LedgerProjectReport>("/bff/investment/reports/projects"),

  getCockpit: () => request<LedgerCockpit>("/bff/investment/reports/cockpit"),

  getProjectPipeline: () =>
    request<ProjectPipeline>("/bff/investment/reports/project-pipeline"),

  getInvestorLibrary: () =>
    request<InvestorLibrary>("/bff/investment/master-data/libraries/investors"),

  getCompanyLibrary: () =>
    request<CompanyLibrary>("/bff/investment/master-data/libraries/companies"),

  getManagerLibrary: () =>
    request<ManagerLibrary>("/bff/investment/master-data/libraries/managers"),

  getTagLibrary: () =>
    request<TagLibrary>("/bff/investment/master-data/libraries/tags"),
};

// ── 项目超期释放配置（设置 › 业务设置）──
/** 项目超期释放配置（租户级）。 */
export type ProjectReleaseConfig = {
  /** 超期天数：项目在当前阶段停留超过该天数且未推进即视为超期。1~3650 的整数。 */
  staleDays: number;
};

/** 项目超期释放配置的读写接口（独立导出，避免改动 investmentApi 既有函数）。 */
export const projectReleaseConfigApi = {
  /** GET /settings/project-release-config；未配置时后端返回默认值 90。 */
  get: () =>
    request<ProjectReleaseConfig>(
      "/bff/investment/settings/project-release-config",
      { method: "GET" }
    ),
  /** PUT /settings/project-release-config，body { staleDays }。 */
  update: (data: ProjectReleaseConfig) =>
    request<ProjectReleaseConfig>(
      "/bff/investment/settings/project-release-config",
      { method: "PUT", data }
    ),
};

export type ProjectFollowOnConfig = {
  followOnDays: number;
  configured?: boolean;
  updatedAt?: string | null;
};

export const projectFollowOnConfigApi = {
  get: () =>
    request<ProjectFollowOnConfig>(
      "/bff/investment/settings/project-follow-on-config"
    ),
  update: (data: Pick<ProjectFollowOnConfig, "followOnDays">) =>
    request<ProjectFollowOnConfig>(
      "/bff/investment/settings/project-follow-on-config",
      { method: "PUT", data }
    ),
  classify: (companyOrganizationUuid: string) =>
    request<{
      mode: "short" | "long";
      previousDate: string | null;
      days: number | null;
      thresholdDays: number;
    }>(
      `/bff/investment/settings/project-follow-on-config/classify?companyOrganizationUuid=${encodeURIComponent(companyOrganizationUuid)}`
    ),
};

export type ProjectCoInvestmentConfig = {
  /** 岗位级别顺序，从低到高；最低岗位及以上具备跟投资格。 */
  minimumPosition: string;
  positionLevels: string[];
  configured?: boolean;
  updatedAt?: string | null;
};

export const projectCoInvestmentConfigApi = {
  get: () =>
    request<ProjectCoInvestmentConfig>(
      "/bff/investment/settings/project-co-investment-config"
    ),
  update: (
    data: Pick<ProjectCoInvestmentConfig, "minimumPosition" | "positionLevels">
  ) =>
    request<ProjectCoInvestmentConfig>(
      "/bff/investment/settings/project-co-investment-config",
      { method: "PUT", data }
    ),
};

export type ProjectManagerRiskMapping = {
  managerMemberUuid: string;
  riskMemberUuid: string;
};

export type ProjectManagerRiskMappingConfig = {
  mappings: ProjectManagerRiskMapping[];
  configured?: boolean;
  updatedAt?: string | null;
};

export const projectManagerRiskMappingApi = {
  get: () =>
    request<ProjectManagerRiskMappingConfig>(
      "/bff/investment/settings/project-manager-risk-mappings"
    ),
  update: (data: Pick<ProjectManagerRiskMappingConfig, "mappings">) =>
    request<ProjectManagerRiskMappingConfig>(
      "/bff/investment/settings/project-manager-risk-mappings",
      { method: "PUT", data }
    ),
};
